import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Truck, MagnifyingGlass, ArrowsCounterClockwise, Warning } from "@phosphor-icons/react";

const catColor = (c) => ({
  plumbing: "bg-cyan-100 text-cyan-800 border-cyan-500",
  electrical: "bg-yellow-100 text-yellow-800 border-yellow-500",
  hvac: "bg-orange-100 text-orange-800 border-orange-500",
  hardware: "bg-slate-100 text-slate-800 border-slate-500",
  paint: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-500",
  cleaning: "bg-emerald-100 text-emerald-800 border-emerald-500",
  safety: "bg-red-100 text-red-800 border-red-500",
  tools: "bg-amber-100 text-amber-800 border-amber-500",
}[c] || "bg-slate-100 text-slate-700 border-slate-400");

const daysSince = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

export default function AdminTruckStockRoster() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    try {
      const r = await api.get("/truck-stock/all");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load roster");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r => {
      if (r.user_name.toLowerCase().includes(needle)) return true;
      return (r.items || []).some(i => `${i.name} ${i.category}`.toLowerCase().includes(needle));
    });
  }, [rows, q]);

  // Combined view — every item across every tech, so office can spot who has any given part
  const allItems = useMemo(() => {
    if (!rows) return [];
    const m = new Map();
    for (const r of rows) {
      for (const i of (r.items || [])) {
        const k = i.name.toLowerCase().trim();
        if (!m.has(k)) m.set(k, { name: i.name, category: i.category, total: 0, techs: [] });
        const bucket = m.get(k);
        bucket.total += parseInt(i.qty) || 0;
        bucket.techs.push({ name: r.user_name, qty: i.qty, unit: i.unit });
      }
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [rows]);

  const toggleExpand = (uid) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  return (
    <AdminShell>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="truck-roster-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Field Mode</div>
            <h1 className="font-display font-black text-3xl uppercase tracking-tight flex items-center gap-2">
              <Truck weight="fill" size={28}/> Truck Stock Roster
            </h1>
            <div className="text-xs text-slate-500 mt-1">Every tech's current van inventory. Search a part to see who's got it before dispatching.</div>
          </div>
          <button onClick={load} data-testid="roster-refresh"
            className="h-10 px-3 border-2 border-slate-800 bg-white hover:bg-slate-100 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
            <ArrowsCounterClockwise size={14}/> Refresh
          </button>
        </div>

        <div className="p-3 bg-white border-2 border-slate-300">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search tech name or part (e.g. GFCI, blinds, P-trap)"
              data-testid="roster-search"
              className="w-full h-10 pl-7 pr-2 border-2 border-slate-300 text-sm"/>
          </div>
        </div>

        {/* By-part summary */}
        {rows && rows.length > 0 && (
          <div className="bg-white border-2 border-slate-300" data-testid="parts-summary">
            <div className="p-3 border-b-2 border-slate-200 font-black uppercase text-xs tracking-widest">
              Across the fleet · {allItems.length} distinct parts
            </div>
            <div className="max-h-64 overflow-y-auto">
              {allItems.slice(0, 40).filter(i => !q.trim() || `${i.name} ${i.category}`.toLowerCase().includes(q.trim().toLowerCase())).map((i, idx) => (
                <div key={idx} className="px-3 py-2 flex items-center gap-3 border-t border-slate-100 hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm truncate">{i.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {i.techs.map((t, ti) => (
                        <span key={ti}>{ti > 0 && " · "}{t.name} ({t.qty} {t.unit})</span>
                      ))}
                    </div>
                  </div>
                  <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border ${catColor(i.category)}`}>{i.category}</span>
                  <span className="font-mono font-black text-lg tabular-nums text-slate-800 w-14 text-right">{i.total}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-tech cards */}
        {rows === null && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
        {rows !== null && rows.length === 0 && (
          <div className="p-6 text-center bg-white border-2 border-dashed border-slate-300">
            <Warning size={28} className="mx-auto text-slate-400"/>
            <div className="mt-2 font-black uppercase text-xs tracking-widest text-slate-600">No tech has scanned their truck yet</div>
            <div className="text-[11px] text-slate-500 mt-1">Ask your crew to open /truck-stock on their phone and tap SCAN MY TRUCK.</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(r => {
            const d = daysSince(r.updated_at);
            const stale = d !== null && d > 14;
            const open = expanded.has(r.user_id);
            return (
              <div key={r.user_id} data-testid={`tech-card-${r.user_id}`}
                className={`border-2 ${stale ? "border-amber-500" : "border-slate-900"} bg-white`}>
                <button onClick={() => toggleExpand(r.user_id)}
                  className="w-full text-left p-3 hover:bg-slate-50 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-lg tracking-tight truncate">{r.user_name || "(unnamed)"}</div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {r.item_count} lines · {r.total_units} units
                      {r.updated_at && <> · updated {d === 0 ? "today" : `${d}d ago`}</>}
                      {stale && <span className="ml-1 text-amber-700">⚠ stale</span>}
                    </div>
                  </div>
                  <span className="text-slate-400 text-lg">{open ? "▾" : "▸"}</span>
                </button>
                {open && (
                  <div className="p-2 border-t-2 border-slate-200 max-h-80 overflow-y-auto">
                    {(r.items || []).map((it, i) => (
                      <div key={i} className="py-1 flex items-center gap-2 border-b border-slate-100 text-xs">
                        <span className={`px-1 py-0.5 text-[9px] font-black uppercase tracking-widest border ${catColor(it.category)}`}>{it.category}</span>
                        <span className="flex-1 font-bold truncate">{it.name}</span>
                        <span className="font-mono text-slate-700">×{it.qty} {it.unit}</span>
                      </div>
                    ))}
                    {(r.items || []).length === 0 && <div className="text-xs italic text-slate-400 p-2">Empty truck.</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}
